import type { UserDataLoginRecord } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import type { ErrorCodes } from 'noddi-ui-web';
import {
  ApiErrorMessage,
  NoddiBasicCard,
  NoddiButton,
  NoddiInfoBanner,
  NoddiPasswordStrength,
  useAppForm,
  useNavigateOnLoginSuccess
} from 'noddi-ui-web';
import { type ReactNode, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { useAuthContext } from '../../contexts/useAuthContext';
import { type UserRoleType } from '../../types/roles';
import { finalizeLoginSession } from './loginSession';
import { createNewPasswordSchema } from './passwordResetSchemas';
import { ResetCountdown } from './ResetCountdown';

type PasswordResetConfirmTranslations = {
  resetYourPassword: string;
  enterNewPassword: string;
  newPassword: string;
  confirmPassword: string;
  resetPassword: string;
  backToLogin: string;
  invalidResetLink: string;
  invalidResetLinkMessage: string;
  passwordResetSuccessful: string;
  passwordResetSuccessfulMessage: string;
  passwordRequired: string;
  passwordMinLength: string;
  confirmPasswordRequired: string;
  passwordsDontMatch: string;
  linkExpiresIn: string;
  linkExpired: string;
};

type PasswordResetConfirmProps = {
  loginPath: string;
  translations?: PasswordResetConfirmTranslations;
  errorCodes?: ErrorCodes;
  /** Optional layout override (e.g. customer branding). */
  layout?: (props: { title: string; children: ReactNode }) => ReactNode;
  /** Where the user lands once the reset signs them in. */
  loginSuccessPath: string;
  /** An account outside these roles is not signed in here; it sees the success screen. */
  requiredLoginLevels?: UserRoleType[];
  trackUserIdentity?: (user: UserDataLoginRecord) => void;
};

const defaultTexts: PasswordResetConfirmTranslations = {
  resetYourPassword: 'Reset your password',
  enterNewPassword: 'Enter your new password below',
  newPassword: 'New password',
  confirmPassword: 'Confirm password',
  resetPassword: 'Reset password',
  backToLogin: 'Back to login',
  invalidResetLink: 'Invalid reset link',
  invalidResetLinkMessage: 'This password reset link is invalid or has expired. Please request a new one.',
  passwordResetSuccessful: 'Password reset successful',
  passwordResetSuccessfulMessage: 'Password reset successful! You can now sign in with your new password.',
  passwordRequired: 'Password is required',
  passwordMinLength: 'Password must be at least 12 characters',
  confirmPasswordRequired: 'Please confirm your password',
  passwordsDontMatch: "Passwords don't match",
  linkExpiresIn: 'The link expires in',
  linkExpired: 'The link has expired. Request a new one to continue.'
};

export const PasswordResetConfirm = ({
  loginPath,
  translations,
  errorCodes,
  layout,
  loginSuccessPath,
  requiredLoginLevels,
  trackUserIdentity
}: PasswordResetConfirmProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginUser } = useAuthContext();
  const { navigateOnLoginSuccess } = useNavigateOnLoginSuccess();
  const [resetSuccess, setResetSuccess] = useState(false);

  const uid = searchParams.get('uid');
  const token = searchParams.get('token');
  const expiresParam = searchParams.get('expires');
  const expiresAtSeconds = expiresParam && Number.isFinite(Number(expiresParam)) ? Number(expiresParam) : undefined;

  const texts = translations ?? defaultTexts;
  const Layout = layout ?? DefaultPasswordResetLayout;

  const { mutateAsync: confirmReset, error } = noddiAsync.usePost({
    type: URLKeys.postPasswordResetConfirm,
    queryConfig: {
      onSuccess: (res) => {
        // No session for an account with an authenticator: it signs in with it.
        const { token: sessionToken, user } = res.data;
        if (!sessionToken || !user) {
          setResetSuccess(true);
          return;
        }
        finalizeLoginSession({
          session: { token: sessionToken, user },
          loginUser,
          requiredLoginLevels,
          accessDeniedMessage: texts.passwordResetSuccessfulMessage,
          onAccessDenied: () => setResetSuccess(true),
          trackUserIdentity,
          onLoginSuccess: () => navigateOnLoginSuccess({ loginSuccessPath })
        });
      }
    }
  });

  const validationSchema = createNewPasswordSchema(texts);
  type FormValues = z.input<typeof validationSchema>;

  const form = useAppForm({
    defaultValues: { password: '', confirmPassword: '' } satisfies FormValues,
    validators: {
      onSubmit: validationSchema
    },
    onSubmit: async ({ value }) => {
      if (!uid || !token) {
        return;
      }
      await confirmReset({ newPassword: value.password, token, uidb64: uid });
    }
  });

  if (!uid || !token) {
    return (
      <Layout title={texts.invalidResetLink}>
        <NoddiInfoBanner variant='error' texts={[{ value: texts.invalidResetLinkMessage }]} />
        {/* oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs navigate(); React discards the returned promise, so a rejection is unhandled unless the body catches */}
        <NoddiButton onClick={() => navigate(loginPath)} className='w-full'>
          {texts.backToLogin}
        </NoddiButton>
      </Layout>
    );
  }

  if (resetSuccess) {
    return (
      <Layout title={texts.passwordResetSuccessful}>
        <NoddiInfoBanner variant='success' texts={[{ value: texts.passwordResetSuccessfulMessage }]} />
        {/* oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs navigate(); React discards the returned promise, so a rejection is unhandled unless the body catches */}
        <NoddiButton onClick={() => navigate(loginPath)} className='w-full'>
          {texts.backToLogin}
        </NoddiButton>
      </Layout>
    );
  }

  return (
    <Layout title={texts.resetYourPassword}>
      <p className='text-sm text-muted-foreground'>{texts.enterNewPassword}</p>
      {expiresAtSeconds !== undefined && (
        <ResetCountdown
          expiresAtSeconds={expiresAtSeconds}
          label={texts.linkExpiresIn}
          expiredLabel={texts.linkExpired}
        />
      )}
      <form.AppForm>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <div className='flex flex-col gap-4'>
            <form.AppField name='password'>
              {(field) => (
                <div className='flex flex-col gap-1'>
                  {/* oxlint-disable-next-line jsx-a11y/no-autofocus -- first field of the password-reset form, which is the entire page */}
                  <field.Input type='password' label={texts.newPassword} autoComplete='new-password' autoFocus />
                  <NoddiPasswordStrength password={field.state.value} />
                </div>
              )}
            </form.AppField>
            <form.AppField name='confirmPassword'>
              {(field) => <field.Input type='password' label={texts.confirmPassword} autoComplete='new-password' />}
            </form.AppField>
            {error && errorCodes && <ApiErrorMessage errorCodes={errorCodes} error={error} />}
            <form.SubscribeButton label={texts.resetPassword} className='w-full' />
            {/* oxlint-disable-next-line typescript/no-misused-promises -- async onClick runs navigate(); React discards the returned promise, so a rejection is unhandled unless the body catches */}
            <NoddiButton type='button' variant='ghost' size='small' onClick={() => navigate(loginPath)}>
              {texts.backToLogin}
            </NoddiButton>
          </div>
        </form>
      </form.AppForm>
    </Layout>
  );
};

const DefaultPasswordResetLayout = ({ title, children }: { title: string; children: ReactNode }) => (
  <div className='flex min-h-svh items-center justify-center'>
    <NoddiBasicCard className='flex w-[93%] max-w-sm flex-col gap-4'>
      <h1 className='text-xl font-medium'>{title}</h1>
      {children}
    </NoddiBasicCard>
  </div>
);
